import Clipboard from "@react-native-clipboard/clipboard";
import {
    Alert,
    DataTable,
    IconButton,
    Loading,
    PageTitle,
    StyleFunction,
    Surface,
    Text,
    useAuth,
    usePagination,
    useTheme,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import { GQAdyenStoresQuery, useAdyenStoresQuery } from "graphql-sdk";
import { useTranslation } from "locales";
import React, { useCallback } from "react";
import { AdminContainer } from "../container";

export function AdyenStoresListScreen() {
    const auth = useAuth();
    auth.enforce(["admin.role"]);

    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const theme = useTheme();
    const toast = useToast();
    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
        initialSortDirection: "DESC",
    });

    const { data, loading, error } = useAdyenStoresQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
        },
        fetchPolicy: "no-cache",
    });

    const renderAdyenStoreRow = useCallback(
        (item: GQAdyenStoresQuery["adyenStores"]["data"][0], rowIndex) => (
            <DataTable.Row
                style={rowIndex % 2 === 0 ? theme.styles.oddRow : undefined}
                key={`merchant_${rowIndex}`}
            >
                <DataTable.Cell>
                    <Text numberOfLines={1}>{item.id}</Text>
                    <IconButton
                        size="small"
                        onPress={() => {
                            Clipboard.setString(item.id as string);
                            toast.info(
                                t("backoffice.info.text_copied", "Text copied")
                            );
                        }}
                        name="copy"
                        color="black"
                        tooltip={t(
                            "backoffice.adyen_store_list.copy_id",
                            "Copy ID"
                        )}
                    />
                </DataTable.Cell>
                <DataTable.Cell>{item.description}</DataTable.Cell>
                <DataTable.Cell>{item.merchantId}</DataTable.Cell>
                <DataTable.Cell>{item.phoneNumber}</DataTable.Cell>
                <DataTable.Cell>{item.reference}</DataTable.Cell>
                <DataTable.Cell>{item.shopperStatement}</DataTable.Cell>
                <DataTable.Cell style={styles.statusColumn}>
                    {item.status}
                </DataTable.Cell>
                <DataTable.Cell>
                    <Text>
                        {item.address?.line1} <br />
                        {item.address?.line2}
                        {item.address?.line2 && <br />}
                        {item.address?.line3}
                        {item.address?.line3 && <br />}
                        {item.address?.postalCode} {item.address?.city}
                        <br />
                        {item.address?.country}
                    </Text>
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [styles.statusColumn, t, theme.styles.oddRow, toast]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error?.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <PageTitle>
                {t("backoffice.adyen_store_list.adyen_stores", "Adyen stores")}
            </PageTitle>

            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title>
                            {t("backoffice.common.id", "ID")}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t("backoffice.common.description", "Description")}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t(
                                "backoffice.adyen_store_list.merchant_id",
                                "Merchant id"
                            )}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t(
                                "backoffice.adyen_store_list.phone_number",
                                "Phone number"
                            )}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t(
                                "backoffice.adyen_store_list.reference",
                                "Reference"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            tooltip={t(
                                "backoffice.adyen_store_list.shopper_statement_description",
                                "The store name shown on the shopper's bank or credit card statement and on the shopper receipt"
                            )}
                        >
                            {t(
                                "backoffice.adyen_store_list.shopper_statement",
                                "Shopper statement"
                            )}
                        </DataTable.Title>
                        <DataTable.Title style={styles.statusColumn}>
                            {t("backoffice.adyen_store_list.status", "Status")}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t(
                                "backoffice.adyen_store_list.address",
                                "Address"
                            )}
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : data.adyenStores.data.length === 0 ? (
                        <DataTable.Row style={theme.styles.oddRow}>
                            <DataTable.Cell>
                                {t(
                                    "backoffice.adyen_terminal_list.no_data",
                                    "No terminals found."
                                )}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ) : (
                        data?.adyenStores.data.map(renderAdyenStoreRow)
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.adyenStores.pagination.pages}
                        itemCount={data?.adyenStores.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    statusColumn: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: theme.spacingScale * 8,
        color: theme.colors.black,
    },
});
